<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>

    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
        
        <!-- Header -->
        <div class="px-6 py-5 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">
              Changer le mot de passe
            </h3>
            <button
              type="button"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              @click="closeModal"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="mt-1 text-sm text-gray-500">
            Entrez votre mot de passe actuel et choisissez un nouveau mot de passe sécurisé.
          </p>
        </div>

        <!-- Formulaire -->
        <form class="px-6 py-6 space-y-6" @submit.prevent="handleSubmit">
          
          <!-- Mot de passe actuel -->
          <div>
            <label for="current-password" class="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe actuel <span class="text-red-500">*</span>
            </label>
            <PasswordField
              id="current-password"
              v-model="formData.currentPassword"
              name="currentPassword"
              placeholder="Votre mot de passe actuel"
              :required="true"
              :error-message="errors.currentPassword"
              :disabled="isSubmitting"
              autocomplete="current-password"
              :left-icon="LockIcon"
              :toggleable="true"
              @blur="validateField('currentPassword')"
            />
          </div>

          <!-- Nouveau mot de passe -->
          <div>
            <label for="new-password" class="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe <span class="text-red-500">*</span>
            </label>
            <PasswordField
              id="new-password"
              v-model="formData.newPassword"
              name="newPassword"
              placeholder="Choisissez un nouveau mot de passe"
              :required="true"
              :error-message="errors.newPassword"
              :disabled="isSubmitting"
              autocomplete="new-password"
              :left-icon="KeyIcon"
              :toggleable="true"
              :show-strength-indicator="true"
              @blur="validateField('newPassword')"
              @strength-change="handlePasswordStrengthChange"
            />
          </div>

          <!-- Confirmation du nouveau mot de passe -->
          <div>
            <label for="confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le nouveau mot de passe <span class="text-red-500">*</span>
            </label>
            <PasswordField
              id="confirm-password"
              v-model="formData.confirmPassword"
              name="confirmPassword"
              placeholder="Confirmez votre nouveau mot de passe"
              :required="true"
              :error-message="errors.confirmPassword"
              :disabled="isSubmitting"
              autocomplete="new-password"
              :left-icon="KeyIcon"
              :toggleable="true"
              @blur="validateField('confirmPassword')"
            />
          </div>

          <!-- Conseils de sécurité -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex">
              <ShieldCheckIcon class="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div class="text-sm text-blue-700">
                <p class="font-medium mb-1">Conseils pour un mot de passe sécurisé :</p>
                <ul class="space-y-1 text-xs">
                  <li>• Au moins 8 caractères</li>
                  <li>• Mélange de lettres majuscules et minuscules</li>
                  <li>• Au moins un chiffre</li>
                  <li>• Au moins un caractère spécial</li>
                  <li>• Évitez les informations personnelles</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Boutons d'action -->
          <div class="flex space-x-3 pt-4">
            <button
              type="button"
              class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Annuler
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isSubmitting || !isFormValid"
            >
              <div class="flex items-center justify-center">
                <LoadingSpinner v-if="isSubmitting" size="sm" variant="white" :show-text="false" />
                <CheckIcon v-else class="h-4 w-4 mr-2" />
                <span>{{ isSubmitting ? 'Modification...' : 'Changer le mot de passe' }}</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import PasswordField from '@/components/form/PasswordField.vue';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/stores/auth';
import type { ChangePasswordRequest } from '@/types/auth';
import {
    Check as CheckIcon,
    Key as KeyIcon,
    Lock as LockIcon,
    ShieldCheck as ShieldCheckIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, reactive, ref, watch } from 'vue';

export interface ChangePasswordModalProps {
  modelValue: boolean;
}

export interface ChangePasswordModalEmits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'success'): void;
  (e: 'error', error: string): void;
}

const props = defineProps<ChangePasswordModalProps>();
const emit = defineEmits<ChangePasswordModalEmits>();

// Composables
const authStore = useAuthStore();

// État du formulaire
const formData = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const errors = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const isSubmitting = ref(false);
const passwordStrength = ref({ score: 0, label: '', percentage: 0 });

// Validation
const validateField = (fieldName: keyof typeof errors): void => {
  const field = fieldName as keyof typeof formData;
  const value = formData[field];

  switch (fieldName) {
    case 'currentPassword':
      if (!value || value.trim().length === 0) {
        errors.currentPassword = 'Le mot de passe actuel est requis';
      } else {
        errors.currentPassword = '';
      }
      break;

    case 'newPassword':
      if (!value || value.trim().length === 0) {
        errors.newPassword = 'Le nouveau mot de passe est requis';
      } else if (value.length < 8) {
        errors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
      } else {
        const validation = authService.validatePassword(value);
        if (!validation.isValid) {
          errors.newPassword = validation.errors.join(', ');
        } else {
          errors.newPassword = '';
        }
      }
      break;

    case 'confirmPassword':
      if (!value || value.trim().length === 0) {
        errors.confirmPassword = 'La confirmation du mot de passe est requise';
      } else if (value !== formData.newPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
      } else {
        errors.confirmPassword = '';
      }
      break;
  }
};

const validateForm = (): boolean => {
  validateField('currentPassword');
  validateField('newPassword');
  validateField('confirmPassword');

  return errors.currentPassword === '' && errors.newPassword === '' && errors.confirmPassword === '' &&
         formData.currentPassword.trim() !== '' && formData.newPassword.trim() !== '' && formData.confirmPassword.trim() !== '';
};

const isFormValid = computed(() => {
  return formData.currentPassword.trim() !== '' && formData.newPassword.trim() !== '' && formData.confirmPassword.trim() !== '' &&
         errors.currentPassword === '' && errors.newPassword === '' && errors.confirmPassword === '' &&
         formData.newPassword === formData.confirmPassword;
});

// Gestionnaires d'événements
const handlePasswordStrengthChange = (strength: { score: number; label: string; percentage: number }): void => {
  passwordStrength.value = strength;
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const changePasswordData: ChangePasswordRequest = {
      current_password: formData.currentPassword,
      new_password: formData.newPassword,
    };

    await authService.changePassword(changePasswordData, authStore.token!);
    
    // Réinitialiser le formulaire
    resetForm();
    
    // Fermer le modal et émettre le succès
    emit('update:modelValue', false);
    emit('success');
    
  } catch (error) {
    console.error('Erreur lors du changement de mot de passe:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe';
    
    // Gérer les erreurs spécifiques
    if (errorMessage.includes('Current password is incorrect')) {
      errors.currentPassword = 'Le mot de passe actuel est incorrect';
    } else {
      emit('error', errorMessage);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = (): void => {
  if (!isSubmitting.value) {
    resetForm();
    emit('update:modelValue', false);
  }
};

const resetForm = (): void => {
  formData.currentPassword = '';
  formData.newPassword = '';
  formData.confirmPassword = '';
  
  errors.currentPassword = '';
  errors.newPassword = '';
  errors.confirmPassword = '';
  
  passwordStrength.value = { score: 0, label: '', percentage: 0 };
};

// Réinitialiser le formulaire quand le modal s'ouvre
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    resetForm();
  }
});
</script> 